import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Row, Table } from 'react-bootstrap';
import { format, parse } from 'date-fns';
import {
  MdDelete,
  MdEdit,
  MdKeyboardArrowRight,
  MdOutlineAddBox
} from 'react-icons/md';
import { debounce } from 'lodash';
import TupleLink from '../TupleLink';
import TableResponsiveCard from '../TableResponsiveCard';
import CardDetailsModal from '../CardDetailsModal';
import ActionColumn from 'screens/common/components/ActionColumn';

const ServerSideTable = ({
  getTableProps,
  headers,
  page,
  prepareRow,
  headerClassName,
  bodyClassName,
  rowClassName,
  tableProps,
  filters,
  handleFilterChange,
  showFilterRow,
  cursor,
  clientSideSearch,
  handleAction, // New prop to handle actions
  responsive,
  cardTittles
}) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);

  const handleOpenModal = rowData => {
    setSelectedRowData(rowData);
    setIsModalOpen(true); // Open modal
  };

  const handleCloseModal = () => {
    setIsModalOpen(false); // Close modal
    setSelectedRowData(null); // Clear selected row data
  };

  const debouncedHandleFilterChange = useMemo(
    () => debounce(handleFilterChange, 100),
    [handleFilterChange]
  );

  const onFilterChange = (columnId, value) => {
    if (clientSideSearch) {
      handleFilterChange(columnId, value);
    } else {
      debouncedHandleFilterChange(columnId, value);
    }
  };

  const renderCollapsibleContent = data => {
    if (!data || typeof data !== 'object') return null;

    return (
      <Row className="gap-6 p-4">
        <Col className="d-flex flex-column mx-4">
          {Object.entries(data).map(([key, value]) => (
            <div key={key}>
              {key === 'title' && (
                <p className="text-uppercase fw-bold mb-2">{value}</p>
              )}
              {key === 'data' && (
                <Row>
                  {/* Reduced column gap */}
                  {(() => {
                    const entries = Object.entries(value);
                    const firstHalf = entries.slice(0, 3);
                    const secondHalf = entries.slice(3);

                    return (
                      <>
                        {/* Left Column - Reduced inner gap */}
                        <Col md={4}>
                          {firstHalf.map(([subKey, subValue], index) => {
                            const text = Array.isArray(subValue)
                              ? subValue[0]
                              : subValue;
                            const color =
                              Array.isArray(subValue) && subValue[1]
                                ? subValue[1]
                                : 'inherit';

                            return (
                              <div
                                key={index}
                                className="d-flex align-items-center gap-1"
                              >
                                <span>
                                  <strong>{subKey}:</strong>
                                </span>
                                <span style={{ color }}>{text}</span>
                              </div>
                            );
                          })}
                        </Col>

                        {/* Right Column*/}
                        <Col md={4}>
                          {secondHalf.map(([subKey, subValue], index) => {
                            const text = Array.isArray(subValue)
                              ? subValue[0]
                              : subValue;
                            const color =
                              Array.isArray(subValue) && subValue[1]
                                ? subValue[1]
                                : 'inherit';

                            return (
                              <div
                                key={index}
                                className="d-flex align-items-center gap-1"
                              >
                                <span>
                                  <strong>{subKey}:</strong>
                                </span>
                                <span style={{ color }}>{text}</span>
                              </div>
                            );
                          })}
                        </Col>
                      </>
                    );
                  })()}
                </Row>
              )}
            </div>
          ))}
        </Col>
      </Row>
    );
  };

  const styles = `
    .expand-icon {
      transition: transform 0.3s ease;
    }

    .expand-icon.expanded {
      transform: rotate(90deg);
    }

    .expanded-content {
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.3s ease-in-out;
    }

    .expanded-content.show {
      max-height: 500px;
    }
    
    .truncate-cell {
      // max-width: 250px; /* Set max width */
      // overflow: hidden;
      // text-overflow: ellipsis;
      // white-space: nowrap;
    }
  `;

  return (
    <>
      <div
        className={`projects-table-wrapper ${
          responsive ? 'd-none d-md-block' : ''
        }`}
        style={{ overflowX: 'auto' }}
      >
        <style>{styles}</style>
        <Table {...getTableProps(tableProps)}>
          <thead className={headerClassName}>
            <tr>
              {page.some(row => row.original.more) && (
                <th style={{ width: 40 }}></th>
              )}
              {headers.map((column, index) => {
                if (column.Header.split(' ')[0] !== 'Section') {
                  //because section need to be avoided
                  return (
                    <th
                      key={index}
                      {...column.getHeaderProps(
                        column.getSortByToggleProps?.(column.headerProps)
                      )}
                      style={{
                        ...(column.isSticky
                          ? {
                              position: 'sticky',
                              left: `${column.stickyOffset}px`,
                              zIndex: 2,
                              backgroundColor: '#F2EFEA',
                              boxShadow: 'none'
                            }
                          : {})
                      }}
                    >
                      {column.render('Header')}
                      {column.canSort && (
                        <span
                          className={`sort ${
                            column.isSorted
                              ? column.isSortedDesc
                                ? 'desc'
                                : 'asc'
                              : ''
                          }`}
                        />
                      )}
                    </th>
                  );
                }
              })}
            </tr>
            {showFilterRow && (
              <tr>
                {page.some(row => row.original.more) && (
                  <th style={{ width: 40 }}></th>
                )}
                {headers.map((column, index) => {
                  if (column.render('Header').split(' ')[0] !== 'Section') {
                    return (
                      <th
                        key={index}
                        style={{
                          ...(column.isSticky
                            ? {
                                position: 'sticky',
                                left: `${column.stickyOffset}px`,
                                zIndex: 2,
                                backgroundColor: '#F2EFEA'
                              }
                            : {})
                        }}
                      >
                        {column.render('Header') !== 'S No' &&
                          column.render('Header') !== 'Action' &&
                          (column
                            .render('Header')
                            .toLowerCase()
                            .includes('date') ||
                          [
                            'created on',
                            'modified on',
                            'transferred on'
                          ].includes(column.render('Header').toLowerCase()) ? (
                            <input
                              type="date" // Render a date input field
                              //placeholder="Select Date"
                              value={
                                filters[column.id]
                                  ? format(
                                      parse(
                                        filters[column.id],
                                        'dd-MM-yyyy',
                                        new Date()
                                      ), // Convert stored value to ISO format
                                      'yyyy-MM-dd'
                                    ) // Convert stored value to ISO format
                                  : ''
                              }
                              onChange={e => {
                                const value = e.target.value;
                                if (value) {
                                  const formattedDate = format(
                                    new Date(value),
                                    'dd-MM-yyyy'
                                  );
                                  onFilterChange(column.id, formattedDate);
                                } else {
                                  onFilterChange(column.id, '');
                                }
                              }}
                              max={format(
                                new Date(
                                  new Date().setDate(new Date().getDate() + 6)
                                ),
                                'yyyy-MM-dd'
                              )}
                              className="w-full p-1 border rounded"
                            />
                          ) : (
                            <input
                              type="text"
                              placeholder="Search"
                              value={filters[column.id] || ''}
                              onChange={e => {
                                onFilterChange(column.id, e.target.value);
                              }}
                              className="w-full p-1 border rounded"
                            />
                          ))}
                      </th>
                    );
                  }
                })}
              </tr>
            )}
          </thead>
          <tbody className={bodyClassName}>
            {page.map((row, i) => {
              prepareRow(row);
              const hasMoreData =
                row.original.more && Object.keys(row.original.more).length > 0;
              const isExpanded = expandedRow === i;

              return (
                <React.Fragment key={i}>
                  <tr {...row.getRowProps()} className={rowClassName}>
                    {hasMoreData && (
                      <td
                        className="w-10 cursor-pointer"
                        onClick={() => setExpandedRow(isExpanded ? null : i)}
                      >
                        <div
                          className={`expand-icon ${
                            isExpanded ? 'expanded' : ''
                          }`}
                        >
                          <MdKeyboardArrowRight size={20} />
                        </div>
                      </td>
                    )}

                    {row.cells.map((cell, cellIndex) => {
                      // Special handling for 'S No' column
                      if (cell.column.Header.split(' ')[0] == 'Section') {
                        return null;
                      }
                      if (cell.column.Header === 'S No') {
                        const sNo = cursor + i + 1;
                        return (
                          <td
                            key={cellIndex}
                            {...cell.getCellProps()}
                            style={{
                              ...(cell.column.isSticky
                                ? {
                                    position: 'sticky',
                                    left: `${cell.column.stickyOffset}px`,
                                    zIndex: 1,
                                    backgroundColor: 'inherit'
                                  }
                                : {})
                            }}
                          >
                            {sNo}
                          </td>
                        );
                      }

                      // Special handling for 'Severity' column
                      if (
                        cell.column.Header === 'Severity' &&
                        typeof cell.value === 'object'
                      ) {
                        return (
                          <td
                            key={cellIndex}
                            {...cell.getCellProps()}
                            style={{
                              ...(cell.column.isSticky
                                ? {
                                    position: 'sticky',
                                    left: `${cell.column.stickyOffset}px`,
                                    zIndex: 1,
                                    backgroundColor: 'inherit'
                                  }
                                : {})
                            }}
                          >
                            <span style={{ color: cell.value.color }}>
                              {cell.value.label}
                            </span>
                          </td>
                        );
                      }

                      // Special handling for columns with an array of strings (e.g., 'risk_ranges')
                      if (
                        Array.isArray(cell.value) &&
                        typeof cell.value[0] === 'string'
                      ) {
                        return (
                          <td
                            key={cellIndex}
                            {...cell.getCellProps()}
                            style={{
                              ...(cell.column.isSticky
                                ? {
                                    position: 'sticky',
                                    left: `${cell.column.stickyOffset}px`,
                                    zIndex: 1,
                                    backgroundColor: 'inherit'
                                  }
                                : {})
                            }}
                          >
                            {/* Map through the array of strings and render each on a new line */}
                            {cell.value.map((item, index) => (
                              <div key={index}>{item}</div>
                            ))}
                          </td>
                        );
                      }

                      // Special handling for 'Action' column
                      if (cell.column.Header == 'Action') {
                        // Get cell props but remove the key
                        const cellProps = cell.getCellProps();
                        delete cellProps.key;

                        return (
                          <ActionColumn
                            key={cellIndex}
                            row={row}
                            handleAction={handleAction}
                            cellProps={{
                              ...cellProps,
                              style: {
                                ...(cell.column.isSticky
                                  ? {
                                      position: 'sticky',
                                      left: `${cell.column.stickyOffset}px`,
                                      zIndex: 1,
                                      backgroundColor: 'inherit'
                                    }
                                  : {})
                              }
                            }}
                          />
                        );
                      }

                      // Default handling for all other columns
                      return (
                        <td
                          key={cellIndex}
                          {...cell.getCellProps()}
                          style={{
                            ...(cell.column.isSticky
                              ? {
                                  position: 'sticky',
                                  left: `${cell.column.stickyOffset}px`,
                                  zIndex: 1,
                                  backgroundColor: 'inherit'
                                }
                              : {})
                          }}
                        >
                          <div
                            className="truncate-cell"
                            style={{
                              maxWidth: '250px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={cell.value}
                          >
                            {cell.value === 0 ? '0' : cell.render('Cell')}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                  {hasMoreData && (
                    <tr className={rowClassName}>
                      <td
                        colSpan={headers.length + (hasMoreData ? 1 : 0)}
                        className="p-0"
                      >
                        <div
                          className={`expanded-content ${
                            isExpanded ? 'show' : ''
                          }`}
                        >
                          {renderCollapsibleContent(row.original.more)}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      </div>
      {/*the responsive card section that works only in smaller screen*/}
      {responsive && (
        <TableResponsiveCard
          page={page}
          cardTittles={cardTittles}
          setIsModalOpen={setIsModalOpen}
          setSelectedRowData={setSelectedRowData}
          handleAction={handleAction}
          renderCollapsibleContent={renderCollapsibleContent}
          prepareRow={prepareRow}
          expandedRow={expandedRow}
        />
      )}

      {/*modal-------------------------- */}

      {/* Modal */}
      {isModalOpen && selectedRowData && (
        <CardDetailsModal
          selectedRowData={selectedRowData}
          cardTittles={cardTittles}
          handleCloseModal={handleCloseModal}
          handleAction={handleAction}
        />
      )}
    </>
  );
};

ServerSideTable.propTypes = {
  getTableProps: PropTypes.func,
  headers: PropTypes.array,
  page: PropTypes.array,
  prepareRow: PropTypes.func,
  headerClassName: PropTypes.string,
  bodyClassName: PropTypes.string,
  rowClassName: PropTypes.string,
  tableProps: PropTypes.object,
  filters: PropTypes.object,
  handleFilterChange: PropTypes.func,
  showFilterRow: PropTypes.bool,
  cursor: PropTypes.number,
  clientSideSearch: PropTypes.bool,
  handleAction: PropTypes.func, // New prop to handle actions
  responsive: PropTypes.bool,
  cardTittles: PropTypes.arrayOf(PropTypes.string)
};

export default ServerSideTable;
